import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowRight, Phone, Clock, GraduationCap, ExternalLink, Sparkles, TrendingUp, AlertTriangle } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { rankLabel } from "@/lib/rankLabel";

// The 2026 Clearing window — keep as a constant so we can date-badge
// content and hide the whole page after the cycle closes without a
// full deploy.
const CLEARING_2026 = {
  opens: "5 Jul 2026",
  results_day_scot: "5 Aug 2026",
  results_day_alevel: "14 Aug 2026",
  closes: "20 Oct 2026",
};

const TIER_COPY = {
  elite: "Elite (Oxbridge / Imperial / LSE tier)",
  russell: "Russell Group mainstream",
  solid: "Solid research / plate-glass",
  mid: "Post-92 / regional",
  broad: "Widening participation",
};


function UniCard({ uni }) {
  return (
    <div
      data-testid={`clearing-uni-card-${uni.id}`}
      className="group border border-stone-200 rounded-lg p-4 bg-white hover:border-stone-500 transition-colors flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-uppercase !text-[10px] text-stone-500">
            {uni.city || "United Kingdom"}
          </div>
          <Link
            to={`/uni/${uni.id}`}
            data-testid={`clearing-uni-name-${uni.id}`}
            className="font-editorial text-lg mt-0.5 block group-hover:underline underline-offset-4 decoration-stone-400 leading-tight"
          >
            {uni.name}
          </Link>
          {rankLabel(uni) && (
            <div className="text-[11px] font-mono text-stone-500 mt-1">
              {rankLabel(uni)}
            </div>
          )}
        </div>
        {uni.logo_url && (
          <img
            src={uni.logo_url}
            alt=""
            className="w-10 h-10 object-contain shrink-0 rounded"
            loading="lazy"
          />
        )}
      </div>

      {uni.note && (
        <p className="text-xs text-stone-600 mt-3 leading-relaxed line-clamp-3">
          {uni.note}
        </p>
      )}

      <div className="mt-auto pt-4 flex items-center gap-2">
        {uni.hotline_url ? (
          <a
            href={uni.hotline_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`clearing-hotline-${uni.id}`}
            className="inline-flex items-center gap-1.5 text-xs bg-stone-900 hover:bg-stone-800 text-white px-3 py-1.5 rounded-full transition-colors"
          >
            <Phone className="w-3 h-3" />
            <span>Clearing page</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <span className="text-xs text-stone-500 italic">Hotline URL pending</span>
        )}
        <Link
          to={`/uni/${uni.id}`}
          className="text-xs text-stone-600 hover:text-stone-900 ml-auto"
          data-testid={`clearing-view-uni-${uni.id}`}
        >
          View WUA profile →
        </Link>
      </div>
    </div>
  );
}


function GradeMatcher() {
  const [curriculum, setCurriculum] = useState("a_level");
  const [value, setValue] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!value.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/clearing/grade-match", {
        curriculum,
        value: value.trim(),
      });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not match. Try AAB, A*AA, or an IB total like 34.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="clearing-grade-matcher"
      className="mt-14 border border-stone-300 rounded-lg bg-[#F7F5F0] p-6 sm:p-8"
    >
      <div className="flex items-start gap-3">
        <GraduationCap className="w-5 h-5 text-stone-700 mt-1 shrink-0" />
        <div>
          <div className="label-uppercase text-stone-500">Grade matcher</div>
          <h2 className="font-editorial text-2xl sm:text-3xl mt-1">
            What tier of Clearing university is realistic with your grades?
          </h2>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            We map your results to a realistic uni band using the same
            equivalency table our matching engine uses everywhere else.
            <span className="italic"> Buckets are indicative — always
            confirm the specific course-level offer.</span>
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
        <Select value={curriculum} onValueChange={setCurriculum}>
          <SelectTrigger
            data-testid="clearing-curriculum-select"
            className="w-full sm:w-52"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="a_level">A Levels (UK)</SelectItem>
            <SelectItem value="ib">IB Diploma</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid="clearing-grade-input"
          placeholder={curriculum === "a_level" ? "e.g. AAB, A*AA, BBC" : "e.g. 34"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="flex-1"
        />
        <Button
          onClick={submit}
          disabled={loading || !value.trim()}
          data-testid="clearing-grade-submit"
          className="bg-stone-900 hover:bg-stone-800 text-white"
        >
          {loading ? "Matching…" : "Match my grades"}
        </Button>
      </div>

      {error && (
        <div
          className="mt-4 text-sm text-red-700 flex items-start gap-2"
          data-testid="clearing-grade-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-8" data-testid="clearing-grade-result">
          <div className="text-xs text-stone-600 mb-4">
            Your predicted tier: <span className="font-semibold">{TIER_COPY[result.student.tier]}</span>
            <span className="text-stone-500 ml-2">
              (a-level rank estimate: {result.student.a_level_rank_estimate})
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {["safe", "match", "reach"].map((kind) => {
              const b = result.buckets[kind];
              return (
                <div key={kind} className="border border-stone-200 rounded-lg bg-white p-4">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        kind === "safe" ? "bg-emerald-600"
                        : kind === "match" ? "bg-amber-500"
                        : "bg-red-600"
                      }`}
                    />
                    <div className="label-uppercase text-stone-500">
                      {kind === "safe" ? "Safer bet" : kind === "match" ? "Match" : "Reach"}
                    </div>
                  </div>
                  <div className="text-xs text-stone-600 mt-2">
                    {TIER_COPY[b.tier]}
                  </div>
                  <ul className="mt-3 space-y-1.5"
                      data-testid={`clearing-bucket-${kind}`}>
                    {b.unis.length === 0 && (
                      <li className="text-xs text-stone-500 italic">
                        No unis in this bucket currently seeded.
                      </li>
                    )}
                    {b.unis.map((u) => (
                      <li key={u.id} className="text-sm">
                        <Link
                          to={`/uni/${u.id}`}
                          className="hover:underline underline-offset-4 decoration-stone-400"
                          data-testid={`clearing-bucket-${kind}-uni-${u.id}`}
                        >
                          {u.name}
                        </Link>
                        {rankLabel(u) && (
                          <span className="text-[10px] font-mono text-stone-400 ml-2">
                            {rankLabel(u)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-stone-500 italic mt-4">
            {result.notes}
          </p>
        </div>
      )}
    </section>
  );
}


export default function Clearing() {
  const [stats, setStats] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState("all");
  const [unis, setUnis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/clearing/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/clearing/subjects").then((r) => setSubjects(r.data.results || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = subject && subject !== "all" ? { subject } : {};
    api.get("/clearing/universities", { params })
      .then((r) => setUnis(r.data.results || []))
      .finally(() => setLoading(false));
  }, [subject]);

  const shown = useMemo(() => unis.slice(0, 60), [unis]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title="UK Clearing 2026 · Which Course Is Actually Worth Taking?"
        description="Every UK university that typically runs Clearing, with our unique outcomes, salary and employer overlay. Grade matcher, hotline links and live UCAS deep link. Editorially curated by WhichUniversityAdvisor."
        image="/og/clearing-2026.png"
        path="/clearing"
      />

      {/* HERO */}
      <div className="grid md:grid-cols-3 gap-8 items-start" data-testid="clearing-hero">
        <div className="md:col-span-2">
          <div className="label-uppercase text-stone-500">UCAS Clearing 2026</div>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            Missed your offer? Here's how to pick a Clearing course you <em>won't regret.</em>
          </h1>
          <p className="mt-6 text-stone-700 max-w-2xl leading-relaxed text-lg">
            UCAS tells you what's <span className="italic">available</span>. WhichUniversityAdvisor tells you what's
            actually <span className="font-semibold">worth taking</span> — salary bands from real LEO data,
            employer destinations, net-cost after scholarships, and our native WUA outcomes rank.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <Link
              to="#grade-matcher"
              className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-4 py-2 rounded-full transition-colors"
              data-testid="clearing-cta-matcher"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Match my grades
            </Link>
            <a
              href="https://www.ucas.com/applying/after-you-apply/clearing-and-results-day"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="clearing-cta-ucas"
              className="inline-flex items-center gap-2 border border-stone-300 hover:border-stone-500 bg-white px-4 py-2 rounded-full transition-colors text-stone-800"
            >
              Search live vacancies on UCAS
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <aside
          data-testid="clearing-key-dates"
          className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-stone-600" />
            <div className="label-uppercase !text-[10px] text-stone-500">Key dates 2026</div>
          </div>
          <ul className="mt-4 space-y-2 text-sm">
            <li className="flex justify-between gap-3">
              <span className="text-stone-600">Clearing opens</span>
              <span className="font-mono">{CLEARING_2026.opens}</span>
            </li>
            <li className="flex justify-between gap-3">
              <span className="text-stone-600">Scottish Highers Results</span>
              <span className="font-mono">{CLEARING_2026.results_day_scot}</span>
            </li>
            <li className="flex justify-between gap-3 font-semibold">
              <span>A-Level Results Day</span>
              <span className="font-mono">{CLEARING_2026.results_day_alevel}</span>
            </li>
            <li className="flex justify-between gap-3">
              <span className="text-stone-600">Clearing closes</span>
              <span className="font-mono">{CLEARING_2026.closes}</span>
            </li>
          </ul>
          {stats && (
            <div className="mt-5 pt-4 border-t border-stone-200 text-xs text-stone-600">
              <span className="font-mono text-stone-900">{stats.runs_clearing}</span> UK universities
              typically run Clearing. <span className="font-mono text-stone-900">{stats.never_runs}</span> almost never do.
            </div>
          )}
        </aside>
      </div>

      {/* EDITORIAL NOTE */}
      <div
        className="mt-10 border-l-2 border-stone-400 pl-4 py-2 text-sm text-stone-600 max-w-3xl italic"
        data-testid="clearing-editorial-note"
      >
        Editorial note: WUA doesn't quote live vacancy counts — that data belongs
        to UCAS and each university's admissions team. What we <span className="italic">do</span> add:
        every course on this page carries the same outcomes, salary and employer
        signals as the rest of our site — so a Clearing decision made in 48 hours
        is still an informed one.
      </div>

      {/* GRADE MATCHER */}
      <div id="grade-matcher">
        <GradeMatcher />
      </div>

      {/* UNI FINDER */}
      <section className="mt-16" data-testid="clearing-uni-finder">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-stone-200 pb-3">
          <div>
            <div className="label-uppercase text-stone-500">Universities running Clearing</div>
            <h2 className="font-editorial text-2xl sm:text-3xl mt-1">
              Every UK uni that typically has Clearing places
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-500 uppercase tracking-wider">Filter</span>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger
                data-testid="clearing-subject-filter"
                className="w-52"
              >
                <SelectValue placeholder="All subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {subjects.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 text-stone-500 text-sm">Loading universities…</div>
        ) : (
          <>
            <div className="mt-4 text-xs text-stone-500">
              Showing {shown.length} of {unis.length} — ranked first, unranked at the end.
            </div>
            <div
              className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
              data-testid="clearing-uni-grid"
            >
              {shown.map((u) => (
                <UniCard key={u.id} uni={u} />
              ))}
            </div>
            {unis.length > shown.length && (
              <p className="mt-6 text-sm text-stone-500 italic">
                Showing top 60 by world rank. Use the subject filter to narrow further.
              </p>
            )}
          </>
        )}
      </section>

      {/* HOW CLEARING WORKS */}
      <section className="mt-20 grid md:grid-cols-3 gap-8" data-testid="clearing-how-it-works">
        <div className="md:col-span-1">
          <div className="label-uppercase text-stone-500">Reference</div>
          <h2 className="font-editorial text-2xl sm:text-3xl mt-1">
            How Clearing actually works
          </h2>
        </div>
        <div className="md:col-span-2 space-y-6 text-sm text-stone-700 leading-relaxed">
          <div>
            <div className="font-editorial text-lg mb-1">1. You become eligible</div>
            <p>You're in Clearing if UCAS shows you have no offers — either you didn't apply, your firm and insurance both rejected you, you declined your offers, or you missed the grades on Results Day.</p>
          </div>
          <div>
            <div className="font-editorial text-lg mb-1">2. You research fast</div>
            <p>Use our <Link to="/advanced" className="underline underline-offset-4 decoration-stone-400">Advanced Search</Link> or the grade matcher above to shortlist ~5 unis and courses that make sense for your grades and career goals. UCAS lists live vacancies on the <a href="https://www.ucas.com/applying/after-you-apply/clearing-and-results-day" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 decoration-stone-400">official UCAS Clearing page</a>.</p>
          </div>
          <div>
            <div className="font-editorial text-lg mb-1">3. You phone the admissions hotline</div>
            <p>Ring each uni's Clearing hotline (link on every card above). They interview you briefly. If they'll take you, they give you a <span className="font-mono">verbal offer</span>.</p>
          </div>
          <div>
            <div className="font-editorial text-lg mb-1">4. You add the choice on UCAS Hub</div>
            <p>Only one Clearing choice at a time. Once you add it and the uni confirms, it becomes your firm — you can no longer swap without contacting the uni.</p>
          </div>
        </div>
      </section>

      {/* WUA ANGLE */}
      <div
        className="mt-20 rounded-lg border border-stone-300 bg-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
        data-testid="clearing-footer-cta"
      >
        <div>
          <div className="label-uppercase text-stone-500">The WUA angle</div>
          <h3 className="font-editorial text-2xl mt-1">
            <TrendingUp className="w-5 h-5 inline mr-1 -mt-1" />
            48 hours to decide? Get a personalised report.
          </h3>
          <p className="text-sm text-stone-600 mt-2 max-w-xl">
            For £49 our WUA Personalised Report (PRE-WUA+) benchmarks any 3 shortlist courses side-by-side
            on salary, employer destinations, net-cost after scholarships and satisfaction — the numbers you
            actually need before ringing a hotline.
          </p>
        </div>
        <Link
          to="/wua-plus/sample"
          data-testid="clearing-cta-wua-plus"
          className="inline-flex items-center gap-2 bg-[#002147] hover:bg-[#001A38] text-white px-5 py-3 rounded-md text-sm"
        >
          See a sample report <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Data provenance */}
      <p className="mt-10 text-xs text-stone-500 italic">
        Data source: hand-curated UK Clearing metadata refreshed annually in June, informed by each university's
        publicly listed Clearing page and UCAS' annual Clearing report. This page does not surface live vacancy
        counts — always confirm live availability on the uni's own Clearing hotline or the official UCAS Clearing page.
      </p>
    </div>
  );
}
